/**
 * Shared contracts for the workflows pages (WP6e / #46629).
 *
 * Extracted from workflows.tsx to keep that file under the ~1,000-line cap
 * (#47040 ratchet). Pure constants/helpers plus the shared button wrapper —
 * no page-level state lives here.
 */

import type { ReactNode } from "react";
import { ApiError } from "@excellent/ui/api";
import type { VerificationEnvironment } from "../env";
import { environmentOfId } from "../env";
import { href } from "../nav";
import { ActionButton } from "../ui";

export const ACTION = {
  scopeAdmitted: "workflows.scope-admitted",
  scopeWaiting: "workflows.scope-waiting",
  scopeRejected: "workflows.scope-rejected",
  connect: "define.setcomposing-true-connect-a-workflow-pages-define-l99",
  detail: "define.onnavigate-href-workflows-candidate-id-detail-pages-define-l183",
  listAdmit: "define.button-pages-define-l193",
  back: "define.onnavigate-href-workflows-back-to-workflows-pages-define-l249",
  openControl: "define.button-pages-define-l263",
  admit: "define.store-admitcandidate-c-id-admit-pages-define-l275",
  cancel: "define.cancel-pages-define-l410",
  createAdmit: "define.cre-pages-define-l413",
  name: "define.e-g-a-refund-above-5-000-was-actually-issued-pages-define-l434",
  actor: "define.e-g-billing-agent-automated-pages-define-l447",
  oracleExternal: "define.choice-pages-define-l457",
  oracleSelf: "define.setoraclekind-self-pages-define-l463",
  oracleDetail: "define.e-g-stripe-api-refund-status-pages-define-l484",
  intakeDoc: "operate.describe-what-the-workflow-guarantees-pages-buyable-l279",
  intakeOracle: "operate.e-g-the-payment-processor-pages-buyable-l287",
  failMode: "operate.setfailmode-c-value-pages-buyable-l297",
  derive: "operate.derive-predicates-pages-buyable-l310",
  bind: "operate.onbind-c-key-bind-this-oracle-pages-buyable-l458",
  toQueue: "operate.onnavigate-href-queue-go-to-the-decision-inbox-pages-buyable-l480",
} as const;

export const STATUS_LABEL: Record<string, string> = {
  draft: "Draft",
  "needs-oracle": "Needs an oracle",
  admitted: "Admitted",
  reject: "Not admitted",
};

export const STATUS_TONE: Record<string, "pass" | "fail" | "unknown" | "neutral"> = {
  draft: "neutral",
  "needs-oracle": "unknown",
  admitted: "pass",
  reject: "neutral",
};

export const BIND_PREREQ =
  "Binding an oracle onto an existing workflow needs a workflow update contract. Use intake to create the workflow with its oracle.";

export function errorMessage(err: unknown): string {
  if (err instanceof ApiError) return err.message;
  if (err instanceof Error) return err.message;
  return "The action did not complete.";
}

export function environmentActor(environment: VerificationEnvironment): string {
  return environment === "demo" ? "demo:act_mw" : "real:act_matt";
}

export function workflowHref(id: string, environment: VerificationEnvironment | null): string {
  void environment;
  return href("workflows", id);
}

export function environmentFromWorkflowId(id: string): VerificationEnvironment | null {
  const marked = environmentOfId(decodeURIComponent(id));
  return marked === "demo" || marked === "real" ? marked : null;
}

export function Btn({
  actionId,
  children,
  onClick,
  variant = "outline",
  size,
  disabled,
  title,
  type = "button",
}: {
  actionId: string;
  children: ReactNode;
  onClick?: () => void;
  variant?: "primary" | "outline" | "ghost";
  size?: "sm";
  disabled?: boolean;
  title?: string;
  type?: "button" | "submit";
}) {
  return (
    <ActionButton
      actionId={actionId}
      data-action-id={actionId}
      variant={variant}
      type={type}
      onClick={onClick}
      disabled={disabled}
      title={title}
    >
      {children}
    </ActionButton>
  );
}
